import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryEntries from "@/pages/home/components/CategoryEntries";
import MovieCard from "@/components/base/MovieCard";
import NewsCard from "@/components/base/NewsCard";
import SectionHeading from "@/components/base/SectionHeading";
import { allMovies, allNews, formatViews, typeLabels } from "@/utils/media";

const tabs = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
  { key: "variety", label: "综艺" },
];

export default function Home() {
  const [tab, setTab] = useState("all");

  const latest = useMemo(
    () => [...allMovies].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
    []
  );

  const hot = useMemo(
    () => [...allMovies].sort((a, b) => b.views - a.views).slice(0, 8),
    []
  );

  const latestNews = useMemo(
    () => [...allNews].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 6),
    []
  );

  const filteredLatest = useMemo(
    () => (tab === "all" ? latest : latest.filter((m) => m.type === tab)).slice(0, 12),
    [tab, latest]
  );

  const spotlight = hot[0];

  return (
    <div className="w-full">
      <h1 className="sr-only">向日葵视频 - 高清影视剧集在线观赏与最新资讯平台</h1>

      <HeroCarousel />
      <CategoryEntries />

      {/* 最新影视推荐 */}
      <section className="w-full max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <SectionHeading
          title="最新影视推荐"
          subtitle="按上线时间为你整理的最新影视作品"
          icon="ri-fire-line"
          moreTo="/category/movie"
          moreLabel="进入频道"
        >
          <div className="hidden md:flex items-center gap-1 bg-background-100 rounded-full p-1">
            {tabs.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setTab(item.key)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  tab === item.key
                    ? "bg-primary-500 text-foreground-950"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </SectionHeading>

        <div className="md:hidden flex items-center gap-1 bg-background-100 rounded-full p-1 mb-5 overflow-x-auto hide-scrollbar">
          {tabs.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                tab === item.key
                  ? "bg-primary-500 text-foreground-950"
                  : "text-foreground-600"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>

        {filteredLatest.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
            {filteredLatest.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        ) : (
          <div className="py-20 text-center text-foreground-500 text-sm bg-background-100 rounded-lg">
            暂无内容，请稍后刷新或更换筛选条件
          </div>
        )}
      </section>

      {/* 热门影视榜单 */}
      <section className="w-full bg-background-100/70 border-y border-background-200/70">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <SectionHeading
            title="热门影视榜单"
            subtitle="根据近期观看与收藏热度实时排序"
            accent="accent"
            icon="ri-trophy-line"
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-lg bg-background-50 border border-background-200/70 p-2 md:p-3">
              {hot.slice(1).map((movie, index) => (
                <Link
                  key={movie.id}
                  to={`/detail/${movie.id}`}
                  className="group flex items-center gap-4 p-2.5 md:p-3 rounded-md hover:bg-primary-50 transition-colors"
                >
                  <span className="w-7 text-center font-heading font-extrabold text-lg text-secondary-400 group-hover:text-primary-600">
                    {index + 2}
                  </span>
                  <div className="w-12 h-16 md:w-14 md:h-[74px] shrink-0 rounded-md overflow-hidden bg-background-200">
                    <img
                      src={movie.cover}
                      alt={movie.title}
                      title={`${movie.title} 在线观看`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-bold text-sm md:text-base text-foreground-950 truncate group-hover:text-primary-700 transition-colors">
                      {movie.title}
                    </h3>
                    <p className="mt-1 text-xs text-foreground-500 truncate">
                      {typeLabels[movie.type]} · {movie.genres.slice(0, 2).join(" / ")}
                    </p>
                    <div className="mt-1.5 flex items-center gap-3 text-xs text-foreground-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line"></i>
                        {formatViews(movie.views)}
                      </span>
                      <span className="flex items-center gap-1 text-primary-600 font-medium">
                        <i className="ri-star-fill"></i>
                        {movie.rating.toFixed(1)}
                      </span>
                    </div>
                  </div>
                  <i className="ri-arrow-right-s-line text-xl text-foreground-300 group-hover:text-primary-600 transition-colors"></i>
                </Link>
              ))}
            </div>

            {spotlight && (
              <Link
                to={`/detail/${spotlight.id}`}
                className="group relative rounded-lg overflow-hidden bg-foreground-950 min-h-[320px]"
              >
                <img
                  src={spotlight.cover}
                  alt={spotlight.title}
                  title={`${spotlight.title} 在线观看`}
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/40 to-transparent"></div>
                <div className="absolute top-4 left-4 h-7 px-3 flex items-center gap-1.5 rounded-full bg-accent-500 text-background-50 text-xs font-bold">
                  <i className="ri-fire-fill"></i>
                  热度 TOP 1
                </div>
                <div className="absolute bottom-0 inset-x-0 p-5">
                  <h3 className="font-heading font-extrabold text-2xl text-background-50">
                    {spotlight.title}
                  </h3>
                  <p className="mt-2 text-sm text-background-200 line-clamp-2">{spotlight.synopsis}</p>
                  <div className="mt-4 flex items-center gap-4 text-xs text-background-100">
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      {formatViews(spotlight.views)} 次观看
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-bookmark-line"></i>
                      {formatViews(spotlight.favorites)} 收藏
                    </span>
                  </div>
                </div>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* 最新影视资讯 */}
      <section className="w-full max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        <SectionHeading
          title="最新影视资讯"
          subtitle="行业动态、深度影评与专题策划"
          accent="secondary"
          icon="ri-newspaper-line"
          moreTo="/news"
        />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          <div className="lg:col-span-2 rounded-lg bg-background-50 border border-background-200/70 px-4 md:px-5">
            {latestNews.slice(0, 4).map((item) => (
              <NewsCard key={item.id} item={item} />
            ))}
          </div>
          <div className="space-y-4">
            {latestNews.slice(4, 6).map((item) => (
              <NewsCard key={item.id} item={item} layout="column" />
            ))}
          </div>
        </div>
      </section>

      <SiteIntro />
    </div>
  );
}

function SiteIntro() {
  return (
    <section className="w-full bg-secondary-900">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-14">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-2">
            <h2 className="font-heading font-extrabold text-2xl md:text-3xl text-background-50">
              关于向日葵视频
            </h2>
            <p className="mt-4 text-sm md:text-base text-background-300 leading-relaxed max-w-2xl">
              向日葵视频是一个面向广大影视爱好者的在线影视推荐与资讯平台。
              我们汇集电影、电视剧、综艺等多元内容，提供清晰的分类导航、多维筛选与关键词搜索，
              帮助你更快找到想看的作品，也及时了解最新影视动态。
            </p>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <p className="font-heading font-extrabold text-2xl md:text-3xl text-primary-500">24+</p>
              <p className="mt-1 text-xs text-background-400">影视作品</p>
            </div>
            <div className="text-center">
              <p className="font-heading font-extrabold text-2xl md:text-3xl text-primary-500">3</p>
              <p className="mt-1 text-xs text-background-400">内容频道</p>
            </div>
            <div className="text-center">
              <p className="font-heading font-extrabold text-2xl md:text-3xl text-primary-500">10+</p>
              <p className="mt-1 text-xs text-background-400">资讯专题</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}